import { observable, action, makeObservable, runInAction } from 'mobx';
import moment from 'moment';
import { RootStore } from './root';
import * as yup from 'yup';
import { NotificationManager } from '../components/notification-manager';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { Lang, LangRegEx } from '../utils';
import { HttpMethod } from '../types';

const LOYALTY_CLUBS_PER_PAGE = 100;

export interface LoyaltyBannerOverlapDetails {
    id: number;
    title: string | null;
    startDate: string;
    endDate: string;
}

export function isLoyaltyBannerOverlapDetails(
    value: unknown
): value is LoyaltyBannerOverlapDetails {
    if (!value || typeof value !== 'object') return false;
    const obj = value as Record<string, unknown>;
    return (
        typeof obj.id === 'number' &&
        (typeof obj.title === 'string' || obj.title === null) &&
        typeof obj.startDate === 'string' &&
        typeof obj.endDate === 'string'
    );
}

export function formatLoyaltyBannerOverlapMessage(
    baseMessage: string,
    details: LoyaltyBannerOverlapDetails
): string {
    const label = details.title?.trim()
        ? `"${details.title.trim()}"`
        : `#${details.id}`;
    const range = `${moment(details.startDate).format(
        'YYYY-MM-DD HH:mm'
    )} → ${moment(details.endDate).format('YYYY-MM-DD HH:mm')}`;
    return `${baseMessage} — conflit avec ${label} (${range})`;
}

const loyaltyBannerItems = yup
    .object({
        title: yup.string().nullable(),
        subtitle: yup.string().nullable(),
        ctaText: yup.string().nullable(),
        image: yup.string().nullable(),
        backgroundColor: yup.string().nullable(),
    })
    .required()
    .shape({
        language: yup.string<Lang>().required().matches(LangRegEx),
    });

export const YupLoyaltyBanner = yup.object({
    id: yup.number().required(),
    isActive: yup.boolean().required(),
    loyaltyClubId: yup.number().nullable(),
    appTarget: yup.string().nullable(),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    icon: yup.string().nullable(),
    translations: yup.array(loyaltyBannerItems).required(),
});

export const YupLoyaltyBannerUpsertPayload = yup.object({
    isActive: yup.boolean().required(),
    loyaltyClubId: yup.number().nullable(),
    appTarget: yup.string().nullable(),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    icon: yup.string().nullable(),
    translations: yup.array(loyaltyBannerItems).required(),
});

const YupLoyaltyBannerList = yup.object({
    total: yup.number().required(),
    results: yup.array(YupLoyaltyBanner).default([]),
});

const YupPaginatedLoyaltyBannerList = YupLoyaltyBannerList.shape({
    page: yup.number().required(),
    perPage: yup.number().required(),
});

const YupLoyaltyClubTranslation = yup.object({
    id: yup.number().required(),
    language: yup.string().required(),
    name: yup.string().required(),
    subtitle: yup.string().nullable(),
    description: yup.string().nullable(),
    image: yup.string().nullable(),
});

const YupLoyaltyClubForPicker = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    active: yup.boolean().required(),
    externalProvider: yup.string().nullable(),
    registrationStartDate: yup.string().required(),
    endDate: yup.string().nullable(),
    translations: yup.array(YupLoyaltyClubTranslation).default([]),
});

const YupLoyaltyClubListResponse = yup.object({
    results: yup.array(YupLoyaltyClubForPicker).default([]),
});

export type PaginatedLoyaltyBannerList = yup.InferType<
    typeof YupPaginatedLoyaltyBannerList
>;
export type LoyaltyBanner = yup.InferType<typeof YupLoyaltyBanner>;
export type LoyaltyBannerUpsertPayload = yup.InferType<
    typeof YupLoyaltyBannerUpsertPayload
>;
export type LoyaltyClubForPicker = yup.InferType<
    typeof YupLoyaltyClubForPicker
>;

export type LoyaltyBannerSearchParams = {
    perPage?: number;
    page?: number;
    q?: string;
};

export class LoyaltyBannerStore extends WithNetworkConcurrency {
    @observable
    paginatedLoyaltyBanners: PaginatedLoyaltyBannerList | null = null;
    @observable isFetchingPaginatedCount = 0;
    @observable loyaltyClubs: LoyaltyClubForPicker[] = [];
    @observable isFetchingLoyaltyClubs = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setPaginatedLoyaltyBanners(
        this: LoyaltyBannerStore,
        loyaltyBanners: PaginatedLoyaltyBannerList
    ) {
        this.paginatedLoyaltyBanners = loyaltyBanners;
    }

    @action
    async fetchPaginatedLoyaltyBanners(
        this: LoyaltyBannerStore,
        searchParams: LoyaltyBannerSearchParams
    ) {
        const tag = this.getTag();
        this.isFetchingPaginatedCount++;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/loyalty-club-banners',
                    params: searchParams,
                },
                YupPaginatedLoyaltyBannerList
            );

            if (!response.err && this.isLatestTag(tag)) {
                this.setPaginatedLoyaltyBanners(response.data);
            }
        } finally {
            runInAction(() => {
                this.isFetchingPaginatedCount--;
            });
        }
    }

    @action
    async fetchLoyaltyBanner(
        this: LoyaltyBannerStore,
        loyaltyBannerId: number
    ) {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: `/loyalty-club-banners/${loyaltyBannerId}`,
            },
            YupLoyaltyBannerUpsertPayload,
            { stripUnknown: true }
        );

        if (!response.err) {
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async updateLoyaltyBanner(
        this: LoyaltyBannerStore,
        loyaltyBanner: LoyaltyBannerUpsertPayload,
        loyaltyBannerId: number
    ) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            url: `/loyalty-club-banners/${loyaltyBannerId}`,
            data: loyaltyBanner,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Loyalty banner updated');
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async createLoyaltyBanner(
        this: LoyaltyBannerStore,
        loyaltyBanner: LoyaltyBannerUpsertPayload
    ) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: `/loyalty-club-banners`,
            data: loyaltyBanner,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Loyalty banner created');
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async deleteLoyaltyBanner(
        this: LoyaltyBannerStore,
        loyaltyBannerId: number
    ) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/loyalty-club-banners/${loyaltyBannerId}`,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Loyalty banner deleted');
        }

        return response;
    }

    @action
    async fetchLoyaltyClubs(this: LoyaltyBannerStore) {
        this.isFetchingLoyaltyClubs = true;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/loyalty/clubs',
                    params: { active: true, perPage: LOYALTY_CLUBS_PER_PAGE },
                },
                YupLoyaltyClubListResponse
            );

            if (!response.err) {
                if (response.data.results.length === LOYALTY_CLUBS_PER_PAGE) {
                    console.warn(
                        '[LoyaltyBannerStore] fetchLoyaltyClubs: 100 clubs returned, list may be truncated'
                    );
                }
                runInAction(() => {
                    this.loyaltyClubs = response.data.results;
                });
            }
        } finally {
            runInAction(() => {
                this.isFetchingLoyaltyClubs = false;
            });
        }
    }
}
